var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ApiErrorStatusCode } from 'lib-shared-api-utils';
import { okta } from 'lib-shared-auth';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginApi } from 'apis/login';
import { appActions } from 'app/app.actions';
import { Sentry } from 'app/utils/sentry';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
/**
 * A hook that resolves the auth data from okta client. This in turn handles the
 * back and forth communication with okta with redirects and login. When the
 * user gets properly logged into okta, the okta tokens are passed to our login
 * api endpoint that exchanges this to auth data.
 */
export const useAuthOktaClient = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    /**
     * Logs in with Okta and gets the auth data for exchanging the Okta token to
     * the auth data. This also triggers redirects in the process.
     */
    const logIn = useCallback((oktaClientAuthMethod) => __awaiter(void 0, void 0, void 0, function* () {
        const loginResult = yield okta.logIn(oktaClientAuthMethod);
        if (loginResult.asyncStatus === 'pending') {
            return Promise.resolve({
                type: 'oktaClient',
                asyncStatus: 'pending',
                pendingReason: loginResult.pendingReason || 'oktaUnknownError',
            });
        }
        // It's a possibility that the tokens could not be fetched after logging
        // into okta. If this happens we redirect to the issuer again.
        if (!loginResult.tokens.accessToken) {
            Sentry.captureEvent({
                message: 'Okta debug - accessToken could not be found.',
                tags: { oktaDebug: true },
                extra: {
                    href: window.location.href,
                },
            });
            okta.redirectToIssuer();
            return Promise.resolve({
                type: 'oktaClient',
                asyncStatus: 'pending',
                pendingReason: 'oktaNoAccessToken',
            });
        }
        // We got the okta tokens, proceed with obtaining the dixa access token
        // using the okta access token.
        return dispatch(loginApi.endpoints.createOktaLogin.initiate({
            accessToken: loginResult.tokens.accessToken.accessToken,
        }))
            .unwrap()
            .then(({ data: authData }) => ({
            type: 'oktaClient',
            asyncStatus: 'fulfilled',
            authData,
        }))
            .catch((error) => {
            if (!error.data) {
                Sentry.captureException(error, {
                    tags: { oktaDebug: true },
                });
            }
            okta.clearTokens();
            // We check for unauthorized as the agent could be assigned in okta
            // but not in the dixa organization.
            if (error.status === ApiErrorStatusCode.Unauthorized) {
                navigate({ search: '' }, { replace: true });
                dispatch(appActions.setError({
                    error: { type: 'agentInsufficientRoles' },
                }));
            }
            else {
                okta.redirectToIssuer();
            }
            return Promise.resolve({
                type: 'oktaClient',
                asyncStatus: 'pending',
                pendingReason: 'oktaFailedToExchangeToken',
            });
        });
    }), [dispatch, navigate]);
    return logIn;
};
